import { Check, ChevronDown, ChevronsUpDown, Plus } from 'lucide-react';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ADD_BUSINESS_PATH } from '@/config/businesses';
import { useBusiness } from '@/hooks/useBusiness';
import { getTemplate } from '@/templates';
import type { BusinessProfile, ID } from '@/types';
import { cn } from '@/utils/cn';

export type SwitcherAction = { kind: 'switch'; id: ID } | { kind: 'add' };

/**
 * What choosing an item does: nothing for the business already active, ask
 * first when there are unsaved edits, otherwise go ahead. Pure, and the only
 * place the rule lives.
 */
export function decideSwitcherAction(
  action: SwitcherAction, activeId: ID, hasUnsavedChanges: boolean,
): 'ignore' | 'confirm' | 'run' {
  if (action.kind === 'switch' && action.id === activeId) return 'ignore';
  return hasUnsavedChanges ? 'confirm' : 'run';
}

interface BusinessSwitcherProps {
  /** `sidebar`: the business card on desktop. `bar`: the name in the phone's top bar. */
  variant: 'sidebar' | 'bar';
  className?: string;
}

/**
 * The active business, as a control that switches it.
 *
 * Switching only moves `settings.activeBusinessId` (`setActiveBusiness`);
 * every business-scoped provider follows that value. It touches no session,
 * plan or subscription, and no business record. The route is kept — every
 * /app section exists for every business — and the page underneath is
 * remounted by `AppShell`, so nothing the previous business had open survives.
 *
 * Unsaved edits are never saved by a switch: a screen that has them says so
 * (`setUnsavedChanges`), and switching or adding asks first.
 */
export function BusinessSwitcher({ variant, className }: BusinessSwitcherProps) {
  const { businesses, activeBusiness, template, setActiveBusiness, hasUnsavedChanges } = useBusiness();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<SwitcherAction | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  // A click or tap anywhere else closes the menu.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  if (!activeBusiness) return null;

  const run = (action: SwitcherAction) => {
    if (action.kind === 'switch') void setActiveBusiness(action.id);
    else navigate(ADD_BUSINESS_PATH);
  };

  const request = (action: SwitcherAction) => {
    setOpen(false);
    triggerRef.current?.focus();
    const decision = decideSwitcherAction(action, activeBusiness.id, hasUnsavedChanges());
    if (decision === 'confirm') setPending(action);
    else if (decision === 'run') run(action);
  };

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
    }
  };

  const target = pending?.kind === 'switch' ? businesses.find((business) => business.id === pending.id) : null;
  const label = `${activeBusiness.name}, current business. Switch business`;

  return (
    // The sidebar menu hangs from the card. The phone menu hangs from the top
    // bar itself (it is sticky, so it positions it), at the bar's own gutter —
    // anchored to the name instead, it ran off a 320px screen.
    <div ref={wrapperRef} className={cn('min-w-0', variant === 'sidebar' && 'relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={label}
        title={activeBusiness.name}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={onTriggerKeyDown}
        className={cn(
          'flex w-full min-w-0 items-center text-left transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
          variant === 'sidebar'
            ? 'gap-2 rounded-xl bg-raised p-3 shadow-xs ring-hairline hover:bg-inset'
            : '-mx-1.5 gap-1 rounded-lg px-1.5 py-1 hover:bg-inset',
        )}
      >
        {variant === 'sidebar' ? (
          <>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-medium text-ink">{activeBusiness.name}</span>
              <span className="mt-0.5 block truncate text-[11px] text-subtle">
                {template?.name} · {activeBusiness.currency}
              </span>
            </span>
            <ChevronsUpDown className="size-3.5 shrink-0 text-subtle" strokeWidth={2.2} aria-hidden />
          </>
        ) : (
          <>
            <span className="truncate text-[14px] font-medium text-ink">{activeBusiness.name}</span>
            <ChevronDown className="size-3.5 shrink-0 text-subtle" strokeWidth={2.2} aria-hidden />
          </>
        )}
      </button>

      {open && (
        <BusinessMenu
          id={menuId}
          businesses={businesses}
          activeId={activeBusiness.id}
          onSelect={(id) => request({ kind: 'switch', id })}
          onAdd={() => request({ kind: 'add' })}
          onClose={(restoreFocus) => {
            setOpen(false);
            if (restoreFocus) triggerRef.current?.focus();
          }}
          className={variant === 'sidebar' ? 'inset-x-0 mt-1.5' : 'left-4 mt-px w-[min(20rem,calc(100vw-2rem))]'}
        />
      )}

      {pending && (
        <ConfirmDialog
          open
          onClose={() => setPending(null)}
          onConfirm={() => run(pending)}
          title="Discard unsaved changes?"
          body={
            target
              ? `Your changes to ${activeBusiness.name} have not been saved. Switching to ${target.name} discards them — nothing is saved.`
              : `Your changes to ${activeBusiness.name} have not been saved. Adding a business leaves this page and discards them.`
          }
          confirmLabel={target ? 'Discard and switch' : 'Discard and continue'}
        />
      )}
    </div>
  );
}

interface BusinessMenuProps {
  id: string;
  /** In the repository's own order. */
  businesses: readonly BusinessProfile[];
  activeId: ID;
  onSelect: (id: ID) => void;
  onAdd: () => void;
  /** `true` when focus should go back to the button that opened it. */
  onClose: (restoreFocus: boolean) => void;
  className?: string;
}

/**
 * The menu itself: one radio item per business, the current one checked, then
 * "Add business". Arrow keys, Home and End move between items; Enter or Space
 * chooses; Escape closes and returns focus; Tab closes. Opens on the current
 * business.
 */
export function BusinessMenu({ id, businesses, activeId, onSelect, onAdd, onClose, className }: BusinessMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const items = menuRef.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]');
    const current = [...(items ?? [])].find((item) => item.getAttribute('aria-checked') === 'true');
    (current ?? items?.[0])?.focus();
  }, []);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const items = [...(menuRef.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? [])];
    const index = items.indexOf(document.activeElement as HTMLElement);
    const next = nextMenuIndex(event.key, index, items.length);
    if (next !== null) {
      event.preventDefault();
      items[next]?.focus();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose(true);
    } else if (event.key === 'Tab') {
      onClose(false);
    }
  };

  return (
    <div
      ref={menuRef}
      id={id}
      role="menu"
      aria-label="Switch business"
      onKeyDown={onKeyDown}
      className={cn('absolute top-full z-40 rounded-xl bg-raised p-1.5 shadow-lg ring-hairline', className)}
    >
      <p className="px-2.5 pb-1 pt-1 text-[11px] font-medium text-subtle" aria-hidden>
        Businesses on this device
      </p>
      <BusinessMenuItems businesses={businesses} activeId={activeId} onSelect={onSelect} onAdd={onAdd} />
    </div>
  );
}

/** The items alone — hook-free, so each one renders and clicks without a browser. */
export function BusinessMenuItems({ businesses, activeId, onSelect, onAdd }: Pick<BusinessMenuProps, 'businesses' | 'activeId' | 'onSelect' | 'onAdd'>) {
  return (
    <>
      <div className="max-h-[min(22rem,55vh)] overflow-y-auto">
        {businesses.map((business) => {
          const isCurrent = business.id === activeId;
          return (
            <button
              key={business.id}
              type="button"
              role="menuitemradio"
              aria-checked={isCurrent}
              tabIndex={-1}
              onClick={() => onSelect(business.id)}
              className={cn(
                'flex w-full min-w-0 items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
                'hover:bg-inset focus-visible:bg-inset focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
              )}
            >
              <span className="min-w-0 flex-1">
                <span className={cn('block truncate text-[13px] text-ink', isCurrent && 'font-semibold')}>{business.name}</span>
                <span className="block truncate text-[11px] text-subtle">
                  {getTemplate(business.templateId).name} · {business.currency}
                </span>
              </span>
              {isCurrent && <Check className="size-4 shrink-0 text-accent-text" strokeWidth={2.4} aria-hidden />}
            </button>
          );
        })}
      </div>
      <div role="separator" className="my-1 h-px bg-hairline" />
      <button
        type="button"
        role="menuitem"
        tabIndex={-1}
        onClick={onAdd}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-medium text-ink transition-colors',
          'hover:bg-inset focus-visible:bg-inset focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        )}
      >
        <Plus className="size-4 shrink-0 text-subtle" strokeWidth={2.2} aria-hidden />
        Add business
      </button>
    </>
  );
}

/** Where focus goes for a key in a menu of `count` items, or null for a key that does not move it. */
export function nextMenuIndex(key: string, index: number, count: number): number | null {
  if (count === 0) return null;
  switch (key) {
    case 'ArrowDown': return index < 0 ? 0 : (index + 1) % count;
    case 'ArrowUp': return index < 0 ? count - 1 : (index - 1 + count) % count;
    case 'Home': return 0;
    case 'End': return count - 1;
    default: return null;
  }
}
